import { createContext, ReactNode, useContext, useMemo } from 'react';
import {
  InsuranceVerificationDetailsResponse,
  PayerDetail,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { ErrorDetails } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { PersonTypes } from '@frontend/api-person';
import { useHasWIVOrEIVAccess } from '@frontend/insurance-verification';
import { useGetPayersList } from '../hooks';
import useGetInsuranceDetails from '../hooks/use-get-insurance-details';
import { InsuranceDetails, PayerListDetails, UndefinedOrNull } from '../utils/types';

export type InsuranceStore = {
  isLoading: boolean;
  personInfo?: PersonTypes.Person;
  insuranceDetails: UndefinedOrNull<InsuranceDetails>;
  hasActiveOrVerifiedInsurance: boolean;
  hasErrors: boolean;
  shouldShowTuuthFairyReport: boolean;
  hasCoverage: boolean;
  isInsuranceStatusInProgress: boolean;
  payersList: UndefinedOrNull<PayerListDetails[]>;
  insuranceVerificationAPIResData: UndefinedOrNull<InsuranceVerificationDetailsResponse>;
  isInsuranceDetailsWritebackSupported: boolean;
  isEnhancedInsuranceVerificationEnabled: boolean;
  isDental: boolean;
  isWellnessVertical: boolean;
  requestId: string;
  isRefetching: boolean;
  locationId: string;
  errorDetails?: ErrorDetails;
  hasInactiveInsuranceDetailsFromPartner?: boolean;
  hasOverrideCoverage?: boolean;
  refetchInsuranceDetails?: () => void;
};

export const InsuranceContext = createContext<InsuranceStore | undefined>(undefined);

type InsuranceProviderProps = {
  children: ReactNode;
  locationId: string;
  personInfo?: PersonTypes.Person;
  disableFetchingVerificationData?: boolean; //This flag is used to disable hitting verification api(3rdpartycoverage) call which is used in schedule pulse flow as we only  hit insurance-details api where we get the primary insurance-verification data from insurance-details api itself so no need of hitting verification-details api
};

export const InsuranceProvider = ({
  children,
  locationId,
  personInfo,
  disableFetchingVerificationData = false,
}: InsuranceProviderProps) => {
  const { isDental, isWellnessVertical, locationIdWiseEIVCustomizationFlagDetails } = useHasWIVOrEIVAccess();
  const isEnhancedInsuranceVerificationEnabled = locationIdWiseEIVCustomizationFlagDetails?.[locationId!] || false;

  const { data: payersList, isLoading: isPayersLoading } = useGetPayersList({
    locationId: locationId || '',
    isEnhancedInsuranceVerificationEnabled,
    enabled: !!locationId,
  });
  const {
    isLoading: isInsuranceDetailsLoading,
    insuranceDetails,
    hasActiveOrVerifiedInsurance,
    hasInactiveInsuranceDetailsFromPartner,
    hasErrors,
    hasCoverage,
    insuranceVerificationAPIResData,
    isInsuranceDetailsWritebackSupported,
    requestId,
    isRefetching,
    errorDetails,
    hasOverrideCoverage,
    refetchInsuranceDetails,
  } = useGetInsuranceDetails(
    locationId,
    personInfo?.PersonID || '',
    personInfo?.SourceID || '',
    payersList as PayerDetail[],
    isDental,
    isWellnessVertical,
    disableFetchingVerificationData
  );

  const isInsuranceStatusInProgress =
    insuranceVerificationAPIResData?.coverage?.thirdPartyCoverage?.verificationStatus === 'IN_PROGRESS';

  const shouldShowTuuthFairyReport =
    insuranceVerificationAPIResData?.coverage?.thirdPartyCoverage?.partner === 'TuuthFairy';
  const isLoading = isInsuranceDetailsLoading || isPayersLoading;

  const value = useMemo<InsuranceStore>(
    () => ({
      isLoading,
      locationId,
      personInfo,
      insuranceDetails,
      payersList,
      hasActiveOrVerifiedInsurance,
      hasInactiveInsuranceDetailsFromPartner,
      isInsuranceStatusInProgress,
      isEnhancedInsuranceVerificationEnabled,
      hasErrors,
      shouldShowTuuthFairyReport,
      hasCoverage,
      insuranceVerificationAPIResData,
      isInsuranceDetailsWritebackSupported,
      requestId: requestId as string,
      isRefetching: isRefetching as boolean,
      errorDetails,
      isDental,
      isWellnessVertical,
      hasOverrideCoverage,
      refetchInsuranceDetails,
    }),
    [
      isLoading,
      isEnhancedInsuranceVerificationEnabled,
      isInsuranceStatusInProgress,
      shouldShowTuuthFairyReport,
      locationId,
      personInfo,
      insuranceDetails,
      payersList,
      hasActiveOrVerifiedInsurance,
      hasInactiveInsuranceDetailsFromPartner,
      hasErrors,
      hasCoverage,
      insuranceVerificationAPIResData,
      isInsuranceDetailsWritebackSupported,
      requestId,
      isRefetching,
      errorDetails,
      isDental,
      isWellnessVertical,
      hasOverrideCoverage,
      refetchInsuranceDetails,
    ]
  );

  return <InsuranceContext.Provider value={value}>{children}</InsuranceContext.Provider>;
};

export const useInsuranceContext = () => {
  const insuranceContext = useContext(InsuranceContext);
  if (!insuranceContext) {
    throw new Error('useInsuranceContext must be used within an InsuranceProvider');
  }
  return insuranceContext;
};
